

























import { Component, Vue, Prop } from 'vue-property-decorator'
import WaveSurfer from 'wavesurfer.js'
import { EventBus, EventBusEvents } from '@/system/eventbus'
import { secondsToClockTime } from '../helpers/date'

@Component({
  components: {
    MiniLoader: require('@/components/MiniLoader').default,
    HelpCircle: require('@/components/HelpCircle').default
  }
})
export default class GlobalWaveformPlayer extends Vue {
  @Prop()
  items: {
    url: string
    name: string
  }[]

  @Prop()
  action1Label: string

  wavesurfer: WaveSurfer
  started: boolean = false
  playing: boolean = false
  loading: boolean = false
  name: string = ''
  visible: boolean = false
  totalTimeSeconds: number = 0
  currentTimeSeconds: number = 0

  currentIndex: number = -1
  nextIndex: number = -1
  prevIndex: number = -1

  get progressLabel(): string {
    if (!this.totalTimeSeconds) return ''

    return `${secondsToClockTime(this.currentTimeSeconds)} / ${secondsToClockTime(this.totalTimeSeconds)}`
  }

  start(url: string) {
    this.$store.commit('player/setUrl', url)

    if (this.wavesurfer) this.wavesurfer.destroy()

    this.visible = true

    if (this.items && this.items.length > 0) {
      this.currentIndex = this.items.findIndex(x => x.url === url)

      // Find prev index with valid url
      this.prevIndex = this.currentIndex

      while (this.prevIndex > 0) {
        this.prevIndex -= 1

        if (this.items[this.prevIndex] && this.items[this.prevIndex].url) {
          break
        }
      }

      if (this.prevIndex < 0) {
        this.prevIndex = -1
      }

      // Same for next index
      this.nextIndex = this.currentIndex

      while (this.nextIndex < this.items.length) {
        this.nextIndex += 1

        if (this.items[this.nextIndex] && this.items[this.nextIndex].url) {
          break
        }
      }

      if (this.nextIndex >= this.items.length) {
        this.nextIndex = -1
      }
    }

    this.enableHotkeys()

    this.loading = true

    EventBus.$emit(EventBusEvents.GlobalWaveformPlay, {
      url,
      name: this.name,
      index: this.currentIndex
    })

    this.wavesurfer = WaveSurfer.create({
      container: this.$refs.waveform as HTMLElement,
      waveColor: '#ff40b4',
      progressColor: '#80205a',
      height: 32,
      barWidth: 1,
      barHeight: 1,
      barGap: 0
    })

    this.wavesurfer.setVolume(0.4)

    this.wavesurfer.on('play', () => {
      this.playing = true

      EventBus.$emit(EventBusEvents.WaveformPlaying, this)

      this.$store.commit('player/setUrl', url)
    })

    this.wavesurfer.on('pause', () => {
      this.playing = false

      this.$store.commit('player/setUrl', null)
    })

    this.wavesurfer.on('audioprocess', (seconds: number) => {
      this.currentTimeSeconds = seconds
    })

    this.wavesurfer.load(url)

    this.wavesurfer.on('ready', async () => {
      this.loading = false
      this.started = true
      this.totalTimeSeconds = this.wavesurfer.getDuration()

      await this.wavesurfer.play()
    })
  }

  async play() {
    if (this.wavesurfer && !this.playing) {
      await this.wavesurfer.play()
    }
  }

  async pause() {
    if (this.wavesurfer && this.playing) {
      await this.wavesurfer.pause()
    }
  }

  previous() {
    this.name = this.items[this.prevIndex].name
    this.start(this.items[this.prevIndex].url)
  }

  next() {
    this.name = this.items[this.nextIndex].name
    this.start(this.items[this.nextIndex].url)
  }

  async close() {
    await this.pause()

    this.visible = false

    this.disableHotkeys()
  }

  performAction1() {
    this.$emit('action1', this.currentIndex)
  }

  async onKeyDown(e: KeyboardEvent) {
    if (e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) return

    if (e.code === 'Space') {
      e.preventDefault()

      if (this.playing) await this.pause()
      else await this.play()
    } else if (e.code === 'Escape') {
      e.preventDefault()

      await this.close()
    } else if (e.key === 'v') {
      e.preventDefault()

      this.next()
    } else if (e.key === 'z') {
      e.preventDefault()

      this.previous()
    } else if (e.key === 'x') {
      e.preventDefault()

      await this.play()
    } else if (e.key === 'c') {
      e.preventDefault()

      await this.pause()
    } else if (e.key === 's') {
      e.preventDefault()

      this.performAction1()
    }
  }

  enableHotkeys() {
    window.addEventListener('keydown', this.onKeyDown)
  }

  disableHotkeys() {
    window.removeEventListener('keydown', this.onKeyDown)
  }

  showHelp() {
    let message = `<strong>Z</strong>: Previous<br>
<strong>X</strong>: Play<br>
<strong>C</strong>: Pause<br>
<strong>V</strong>: Next<br>
<strong>Space</strong>: Play/pause<br>
<strong>Escape</strong>: Close`

    if (this.action1Label) {
      message += `<br><strong>S</strong>: ${this.action1Label}`
    }

    this.$buefy.dialog.alert({
      title: 'Keyboard shortcuts',
      message,
      type: 'is-info'
    })
  }

  mounted() {
    EventBus.$on(EventBusEvents.WaveformPlaying, async (vm: Vue) => {
      if ((this as any)._uid === (vm as any)._uid) return

      await this.pause()
    })

    EventBus.$on(EventBusEvents.GlobalWaveformStart, async ({ url, name }: { url: string, name: string }) => {
      this.start(url)
      this.name = name
    })

    EventBus.$on(EventBusEvents.GlobalWaveformClose, this.close)
  }

  destroyed() {
    if (this.wavesurfer) this.wavesurfer.destroy()

    EventBus.$off(EventBusEvents.WaveformPlaying)
    EventBus.$off(EventBusEvents.GlobalWaveformStart)
    EventBus.$off(EventBusEvents.GlobalWaveformClose)

    this.disableHotkeys()
  }
}
